import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { api } from '../../utils/apiClient';
import { TokenTransaction, TokenTransactionType } from '../../utils/types';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../common/Pagination';
import { config } from '../../config';
import TransactionFilters, { TimeframeOption } from '../common/TransactionFilters';

/**
 * TokenTransactionHistory component displays a user's token movements
 * Shows all debits and credits from various sources with pagination
 */
function TokenTransactionHistory() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<TimeframeOption>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Use pagination hook
  const { 
    currentPage, 
    paginate, 
    totalPages,
    itemsPerPage,
    itemsPerPageOptions,
    handleItemsPerPageChange,
    getCurrentItems
  } = usePagination({
    totalItems: transactions.length,
    defaultItemsPerPage: config.pagination.defaultItemsPerPage
  });

  // Get current page transactions
  const { currentItems: paginatedTransactions } = getCurrentItems(transactions);

  // Format a date with both date and time
  const formatDateTime = (dateStr: string | Date) => {
    if (!dateStr) return '-';
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Load transactions
  const loadTransactions = useCallback(async () => {
    // Clear any previous errors first
    setError(null);
    
    if (!user.token) {
      setIsLoading(false);
      setError(t('errors.loginRequired', 'You must be logged in to view transactions'));
      return;
    }

    setIsRefreshing(true);

    try {
      // First verify the user's authentication is valid
      const authCheck = await api.auth.verifyToken();
      if (!authCheck.success) {
        setError(t('errors.authRequired', 'Authentication is required. Please log in again.'));
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }
      
      // Use email from user context since it might not be in the verification response
      if (!user.email) {
        setError(t('errors.userInfoMissing', 'User information is missing. Please log in again.'));
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }
      
      // Now use the authenticated user's email to fetch transactions (no email needed in call)
      const response = await api.wallet.getTokenTransactions(timeframe);
      
      if (response.success && response.transactions) {
        // Format dates
        const formattedTransactions: TokenTransaction[] = response.transactions.map(tx => ({
          ...tx,
          date: tx.date instanceof Date ? tx.date : new Date(tx.date)
        }));
        
        setTransactions(formattedTransactions);
      } else {
        // Show error message if API call was not successful
        setError(response.message || t('errors.failedToFetchHistory', 'Failed to fetch transaction history'));
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching token transactions:', err);
      setError(t('errors.failedToFetchHistory', 'Failed to fetch transaction history. Please try again later.'));
      setTransactions([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [timeframe, t, user.token, user.email]);

  // Initial load
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe: TimeframeOption) => {
    setTimeframe(newTimeframe);
    // Reset to first page when filter changes
    paginate(1);
  };

  // Get transaction type badge
  const getTransactionBadge = (type: TokenTransactionType) => {
    switch (type) {
      case TokenTransactionType.PURCHASE:
        return <span className="badge bg-success">Purchase</span>;
      case TokenTransactionType.CLICK_DEBIT:
        return <span className="badge bg-danger">Button Tap</span>;
      case TokenTransactionType.CLICK_CREDIT:
        return <span className="badge bg-info">Earning</span>;
      case TokenTransactionType.GIFT:
        return <span className="badge bg-warning text-dark">Gift</span>;
      case TokenTransactionType.WITHDRAWAL:
        return <span className="badge bg-primary">Withdrawal</span>;
      default:
        return <span className="badge bg-secondary">Other</span>;
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Header with back button */}
          <div className="d-flex align-items-center mb-4">
            <Link to="/wallet" className="btn btn-outline-secondary me-3">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h1 className="h3 mb-0">{t('transactions.title', 'Token Transaction History')}</h1>
          </div>

          {/* Filter controls using shared component */}
          <TransactionFilters
            timeframe={timeframe}
            onTimeframeChange={handleTimeframeChange}
            onRefresh={loadTransactions}
            isRefreshing={isRefreshing}
          />

          {/* Main content */}
          <div className="border rounded">
            {isLoading && !isRefreshing ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
                </div>
                <p className="mt-3 text-muted">{t('transactions.loading', 'Loading your transaction history...')}</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center p-5">
                <i className="bi bi-receipt text-muted" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">{t('transactions.noTransactions', 'No Transaction History')}</h5>
                <p className="text-muted">{t('transactions.noTransactionsDesc', 'You haven\'t had any token transactions yet.')}</p>
                <Link to="/wallet" className="btn btn-primary mt-2">
                  <i className="bi bi-wallet2 me-2"></i>
                  {t('transactions.goToWallet', 'Go to Wallet')}
                </Link>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 text-nowrap">{t('transactions.date', 'Date & Time')}</th>
                      <th className="border-0 d-none d-sm-table-cell">{t('transactions.type', 'Type')}</th>
                      <th className="border-0">{t('transactions.description', 'Description')}</th>
                      <th className="border-0 text-end">{t('transactions.amount', 'Amount')}</th>
                      <th className="border-0 text-end d-none d-md-table-cell">{t('transactions.balance', 'Balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="text-nowrap small">{formatDateTime(transaction.date)}</td>
                        <td className="d-none d-sm-table-cell">{getTransactionBadge(transaction.type as TokenTransactionType)}</td>
                        <td>
                          <div className="d-flex flex-column">
                            <div>
                              {(() => {
                                // Split description to get first line only for the main display
                                if (!transaction.description) return '-';
                                
                                const lines = transaction.description.split('\n');
                                // Show badge on mobile when type column is hidden
                                return (
                                  <>
                                    <span className="d-inline d-sm-none me-1">
                                      {getTransactionBadge(transaction.type as TokenTransactionType)}
                                    </span>
                                    <span>{lines[0]}</span>
                                  </>
                                );
                              })()}
                            </div>
                            {transaction.description && transaction.description.includes('\n') && (
                              <small className="text-muted">
                                {(() => {
                                  // Get the lines after the first one
                                  const lines = transaction.description.split('\n').slice(1);
                                  const detailsLine = lines.join('\n');
                                  
                                  // Check if this is a button/counter description format
                                  if (detailsLine.includes('Button name:') && detailsLine.includes('Tap Counter name:')) {
                                    // Split by the comma between button and counter
                                    const parts = detailsLine.split(', Tap Counter name:');
                                    return (
                                      <>
                                        {parts[0]}
                                        <br />
                                        {'Tap Counter name:' + parts[1]}
                                      </>
                                    );
                                  }
                                  
                                  // Otherwise return the original text
                                  return detailsLine;
                                })()}
                              </small>
                            )}
                          </div>
                        </td>
                        <td className="text-end text-nowrap">
                          <span className={transaction.amount >= 0 ? 'text-success' : 'text-danger'}>
                            {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                          </span>
                          {/* Show balance on small screens within the amount column */}
                          {typeof transaction.balanceAfter === 'number' && (
                            <div className="small text-muted d-sm-none">
                              {t('transactions.balanceShort', 'Bal')}: {transaction.balanceAfter.toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="text-end text-nowrap d-none d-md-table-cell">
                          {typeof transaction.balanceAfter === 'number' ? transaction.balanceAfter.toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {transactions.length > 0 && (
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                paginate={paginate}
                itemsPerPage={itemsPerPage}
                totalItems={transactions.length}
                itemsPerPageOptions={itemsPerPageOptions}
                handleItemsPerPageChange={handleItemsPerPageChange}
                ariaLabel={t('pagination.tokenHistoryPagination', 'Token history pagination')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TokenTransactionHistory; 